/**
 * Allergen catalog, tray panels, allergen profiles and region defaults
 * (docs/specs/2026-10-02-allergen-catalog-design.md). The value lists here are
 * the single source: the database schema imports them.
 */
import { z } from "zod";

export const ALLERGEN_CATEGORIES = ["pollen-tree", "pollen-grass", "pollen-weed", "mold", "epidermal", "insect", "mite", "control"] as const;
export const CONTROL_KINDS = ["negative", "positive"] as const;
export const EXPOSURES = ["indoor", "outdoor", "both"] as const;
export const ANIMAL_CLASSES = ["dog", "cat", "bird", "guinea_pig", "cattle", "horse"] as const;
export const CATALOG_ITEM_TYPES = ["extract", "control", "diluent", "vial", "device", "supply"] as const;
export const PROCUREMENT_STATUSES = ["preferred", "acceptable_substitute", "do_not_order"] as const;
export const RECIPE_STATUSES = ["active", "retired"] as const;
export const FAMILY_STATUSES = ["active", "retired"] as const;
export const VERSION_STATUSES = ["draft", "published", "retired"] as const;
export const FILL_SOURCES = ["product", "mix_recipe"] as const;
export const PREVALENCE_GRADES = ["A", "B", "C", "D"] as const;
export const SEVERITY_GRADES = ["A", "B", "C"] as const;
export const SEASONS = ["spring", "summer", "fall", "winter"] as const;
export const TRAY_POSITIONS = 60;
export const NEGATIVE_CONTROL_POSITION = 59;
export const POSITIVE_CONTROL_POSITION = 60;

const id = z.string().uuid();
const nullableText = z.string().nullable();

// ---- Antigens, products, recipes ----

export const CatalogItemSummary = z.object({
  id,
  itemType: z.enum(CATALOG_ITEM_TYPES),
  labeler: nullableText,
  supplier: nullableText,
  manufacturerItemNumber: nullableText,
  manufacturerProductName: z.string(),
  potencyLabelText: nullableText,
  ndc11: nullableText,
  ndcStatus: nullableText,
  procurementStatus: z.enum(PROCUREMENT_STATUSES),
});
export const CatalogItemDetail = CatalogItemSummary.extend({
  supplierItemNumber: nullableText,
  potencyValue: nullableText,
  potencyUnit: nullableText,
  standardized: z.boolean().nullable(),
  diluentBase: nullableText,
  packageVolumeMl: z.number().nullable(),
  ndc10: nullableText,
  ndcConfiguration: nullableText,
  ndcNoneReason: nullableText,
  marketingStatus: nullableText,
  legacyProductId: z.number().int().nullable(),
  opsItemCode: nullableText,
  vialCapColor: nullableText,
  notes: nullableText,
  antigens: z.array(z.object({ id, displayName: z.string(), abbreviation: z.string() })),
});
export const UpdateCatalogItemRequest = z
  .object({
    procurementStatus: z.enum(PROCUREMENT_STATUSES),
    ndcStatus: nullableText,
    ndcNoneReason: nullableText,
    marketingStatus: nullableText,
    opsItemCode: nullableText,
    vialCapColor: nullableText,
    notes: nullableText,
  })
  .partial()
  .strict();

export const AntigenSummary = z.object({
  id,
  legacyAntigenId: z.number().int().nullable(),
  displayName: z.string(),
  abbreviation: z.string(),
  category: z.enum(ALLERGEN_CATEGORIES),
  controlKind: z.enum(CONTROL_KINDS).nullable(),
  isOffered: z.boolean(),
  productCount: z.number().int(),
});
export const AntigenDetail = AntigenSummary.extend({
  a123Name: nullableText,
  genusSpecies: nullableText,
  crossReactionGroup: z.number().int().nullable(),
  exposure: z.enum(EXPOSURES).nullable(),
  animalClass: z.enum(ANIMAL_CLASSES).nullable(),
  diagnosisCode: nullableText,
  highProtease: z.boolean(),
  products: z.array(CatalogItemSummary.extend({ equivalenceNote: nullableText })),
});
export const UpdateAntigenRequest = z
  .object({
    displayName: z.string().trim().min(1),
    abbreviation: z.string().trim().min(1).max(10),
    genusSpecies: nullableText,
    category: z.enum(ALLERGEN_CATEGORIES),
    controlKind: z.enum(CONTROL_KINDS).nullable(),
    crossReactionGroup: z.number().int().nullable(),
    exposure: z.enum(EXPOSURES).nullable(),
    animalClass: z.enum(ANIMAL_CLASSES).nullable(),
    diagnosisCode: nullableText,
    highProtease: z.boolean(),
    isOffered: z.boolean(),
  })
  .partial()
  .strict();
export const LinkProductRequest = z.object({ catalogItemId: id, equivalenceNote: z.string().optional() });

export const MixRecipe = z.object({
  id,
  name: z.string(),
  version: z.number().int(),
  status: z.enum(RECIPE_STATUSES),
  mixAntigenId: id,
  mixAntigenName: z.string(),
  components: z.array(z.object({ antigenId: id, displayName: z.string(), parts: z.number().int().positive() })),
});
export const CreateMixRecipeRequest = z.object({
  name: z.string().trim().min(2),
  mixAntigenId: id,
  components: z.array(z.object({ antigenId: id, parts: z.number().int().positive() })).min(2),
});

export const AntigensResponse = z.object({ antigens: z.array(AntigenSummary) });
export const CatalogItemsResponse = z.object({ items: z.array(CatalogItemSummary) });
export const MixRecipesResponse = z.object({ recipes: z.array(MixRecipe) });

// ---- Families and versions (shared by panels and profiles) ----

export const VersionRef = z.object({ id, version: z.number().int(), status: z.enum(VERSION_STATUSES), publishedAt: z.string().nullable() });
export const FamilySummary = z.object({
  id,
  code: z.string(),
  name: z.string(),
  status: z.enum(FAMILY_STATUSES),
  published: VersionRef.nullable(),
  draft: VersionRef.nullable(),
  versions: z.number().int(),
});
export const FamiliesResponse = z.object({ families: z.array(FamilySummary) });
export const CreateFamilyRequest = z.object({
  code: z.string().trim().toUpperCase().regex(/^[A-Z0-9-]{2,12}$/),
  name: z.string().trim().min(2),
});
export const CreateDraftRequest = z.object({ fromVersionId: id.nullable() });
export const CreateDraftResponse = z.object({ draft: VersionRef, created: z.boolean() });
export const RuleIssue = z.object({ code: z.string(), position: z.number().int().optional(), antigenId: id.optional(), message: z.string() });
export const CheckResponse = z.object({ issues: z.array(RuleIssue) });
export const PublishRequest = z.object({ note: z.string().trim().max(500).optional() });

const versionHeader = {
  id,
  family: z.object({ id, code: z.string(), name: z.string() }),
  version: z.number().int(),
  status: z.enum(VERSION_STATUSES),
  basedOnVersionId: id.nullable(),
  effectiveFrom: z.string().nullable(),
  publishedAt: z.string().nullable(),
  publishedByUserId: id.nullable(),
  note: nullableText,
};

// ---- Tray panels ----

export const PanelPosition = z.object({
  position: z.number().int().min(1).max(TRAY_POSITIONS),
  block: z.string(),
  arm: z.enum(["right", "left"]),
  antigenId: id,
  displayName: z.string(),
  abbreviation: z.string(),
  trayLabel: z.string(),
  fillSource: z.enum(FILL_SOURCES),
  mixRecipeId: id.nullable(),
});
export const PanelVersionDetail = z.object({ ...versionHeader, positions: z.array(PanelPosition) });
export const PositionInput = z.object({
  position: z.number().int().min(1).max(TRAY_POSITIONS),
  antigenId: id,
  trayLabel: z.string().trim().toUpperCase().min(1).max(10),
  fillSource: z.enum(FILL_SOURCES),
  mixRecipeId: id.nullable().default(null),
});
export const SavePositionsRequest = z.object({ positions: z.array(PositionInput).min(1).max(TRAY_POSITIONS) });
const PositionLite = z.object({ antigenId: id, displayName: z.string(), trayLabel: z.string(), fillSource: z.enum(FILL_SOURCES) });
export const PanelDifference = z.object({ position: z.number().int(), a: PositionLite.nullable(), b: PositionLite.nullable() });
export const PanelCompareResponse = z.object({ a: z.object({ ...versionHeader }), b: z.object({ ...versionHeader }), differences: z.array(PanelDifference) });

// ---- Allergen profiles ----

export const ProfileEntry = z.object({
  antigenId: id,
  displayName: z.string(),
  abbreviation: z.string(),
  prevalence: z.enum(PREVALENCE_GRADES).nullable(),
  severity: z.enum(SEVERITY_GRADES).nullable(),
  seasons: z.array(z.enum(SEASONS)),
});
export const ProfileVersionDetail = z.object({ ...versionHeader, entries: z.array(ProfileEntry) });
export const EntryInput = z.object({
  antigenId: id,
  prevalence: z.enum(PREVALENCE_GRADES).nullable(),
  severity: z.enum(SEVERITY_GRADES).nullable(),
  seasons: z.array(z.enum(SEASONS)).max(SEASONS.length),
});
export const SaveEntriesRequest = z.object({ entries: z.array(EntryInput).min(1) });
const EntryLite = z.object({ prevalence: z.enum(PREVALENCE_GRADES).nullable(), severity: z.enum(SEVERITY_GRADES).nullable(), seasons: z.array(z.enum(SEASONS)) });
export const ProfileDifference = z.object({ antigenId: id, displayName: z.string(), a: EntryLite.nullable(), b: EntryLite.nullable() });
export const ProfileCompareResponse = z.object({ a: z.object({ ...versionHeader }), b: z.object({ ...versionHeader }), differences: z.array(ProfileDifference) });

// ---- Region defaults and locations ----

export const RegionDefault = z.object({
  id,
  state: z.string(),
  zipPrefix: z.string().nullable(),
  trayPanelId: id,
  trayPanelCode: z.string(),
  allergenProfileId: id,
  allergenProfileCode: z.string(),
});
export const RegionDefaultsResponse = z.object({ defaults: z.array(RegionDefault) });
export const UpsertRegionDefaultRequest = z.object({
  state: z.string().trim().toUpperCase().regex(/^[A-Z]{2}$/),
  zipPrefix: z.string().trim().regex(/^\d{3}$/).nullable(),
  trayPanelId: id,
  allergenProfileId: id,
});
export const ResolveRegionDefaultResponse = z.object({ default: RegionDefault.nullable(), matchedBy: z.enum(["zip_prefix", "state"]).nullable() });
export const LocationCatalog = z.object({ id, name: z.string(), trayPanelId: id.nullable(), allergenProfileId: id.nullable() });
export const OrganizationLocationsResponse = z.object({ locations: z.array(LocationCatalog) });
export const SetLocationCatalogRequest = z.object({ trayPanelId: id.nullable(), allergenProfileId: id.nullable() });

export type CatalogItemSummary = z.infer<typeof CatalogItemSummary>;
export type CatalogItemDetail = z.infer<typeof CatalogItemDetail>;
export type UpdateCatalogItemRequest = z.infer<typeof UpdateCatalogItemRequest>;
export type AntigenSummary = z.infer<typeof AntigenSummary>;
export type AntigenDetail = z.infer<typeof AntigenDetail>;
export type UpdateAntigenRequest = z.infer<typeof UpdateAntigenRequest>;
export type MixRecipe = z.infer<typeof MixRecipe>;
export type CreateMixRecipeRequest = z.infer<typeof CreateMixRecipeRequest>;
export type AntigensResponse = z.infer<typeof AntigensResponse>;
export type CatalogItemsResponse = z.infer<typeof CatalogItemsResponse>;
export type MixRecipesResponse = z.infer<typeof MixRecipesResponse>;
export type VersionRef = z.infer<typeof VersionRef>;
export type FamilySummary = z.infer<typeof FamilySummary>;
export type FamiliesResponse = z.infer<typeof FamiliesResponse>;
export type CreateFamilyRequest = z.infer<typeof CreateFamilyRequest>;
export type CreateDraftResponse = z.infer<typeof CreateDraftResponse>;
export type RuleIssue = z.infer<typeof RuleIssue>;
export type CheckResponse = z.infer<typeof CheckResponse>;
export type PanelPosition = z.infer<typeof PanelPosition>;
export type PanelVersionDetail = z.infer<typeof PanelVersionDetail>;
export type PositionInput = z.infer<typeof PositionInput>;
export type PanelDifference = z.infer<typeof PanelDifference>;
export type PanelCompareResponse = z.infer<typeof PanelCompareResponse>;
export type ProfileEntry = z.infer<typeof ProfileEntry>;
export type ProfileVersionDetail = z.infer<typeof ProfileVersionDetail>;
export type EntryInput = z.infer<typeof EntryInput>;
export type ProfileDifference = z.infer<typeof ProfileDifference>;
export type ProfileCompareResponse = z.infer<typeof ProfileCompareResponse>;
export type RegionDefault = z.infer<typeof RegionDefault>;
export type RegionDefaultsResponse = z.infer<typeof RegionDefaultsResponse>;
export type UpsertRegionDefaultRequest = z.infer<typeof UpsertRegionDefaultRequest>;
export type ResolveRegionDefaultResponse = z.infer<typeof ResolveRegionDefaultResponse>;
export type LocationCatalog = z.infer<typeof LocationCatalog>;
export type OrganizationLocationsResponse = z.infer<typeof OrganizationLocationsResponse>;
export type SetLocationCatalogRequest = z.infer<typeof SetLocationCatalogRequest>;
export type Season = (typeof SEASONS)[number];
